import { Attribute } from '../../decorators/attribute.decorator';
import { Component } from '../../decorators/component.decorator';
import { MenuOverlayCSSClass, TopModalCSSClass } from '../../global/constants';
import {
    appendModalControllerIfRequired,
    runInNextFrame,
    waitForAnimations,
} from '../../global/helpers';
import { browser, eventBus } from '../../global/index';
import {
    handleMenuOnResize,
    handleTooltipsOnResize,
    hasLastOverlay,
    mainMenuOverlayHandler,
    modalOverlayHandler,
    OverlayWatchEvents,
    type ResizeCallbackArgs,
} from '../../global/overlay';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { UINav } from '../menu/ui-nav';
import type { UIModalController } from '../modal/ui-modal-controller';
import { UIModalUpdateListDetail } from '../modal/ui-modal-controller.types';
import type { UIModal } from '../modal/ui-modal';
import { UIElement } from '../ui-element';
import styles from './ui-overlay-controller.css';
import type {
    UIOverlayControllerTriggerAction,
    UIOverlayControllerTriggerActions,
} from './ui-overlay-controller.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOverlayController
 * @element ui-overlay-controller
 * @classdesc Represents a class for <code>ui-overlay-controller</code> element.
 * @property {boolean} [opened] {@attr opened}
 * @property {string} [mainMenuSelector] {@attr main-menu-selector}
 * @property {UIModalController} modalController Shortcut to ui-modal-controller.
 * @property {UINav | Element | null} mainMenu Shortcut to ui-nav main menu.
 * @property {HTMLElement} root root Shortcut to root element.
 * @property {UIModal | undefined} lastModal Contains a last modal if exists.
 * @slot
 * @example
 * <ui-overlay-controller></ui-overlay-controller>
 */
@Component({ tag: 'ui-overlay-controller', styles })
export class UIOverlayController extends UIElement {
    @Attribute(Boolean) opened?: boolean;
    @Attribute(String) mainMenuSelector: string = '#nav-main';

    public lastModal: UndefinedOr<UIModal>;
    public menuOpened?: boolean;

    private overlayHandler!: (
        event: CustomEvent<UIModalUpdateListDetail>
    ) => void;

    static readonly TriggerAction: UIOverlayControllerTriggerActions = {
        CloseEnter: 'modal-close-enter',
        OpenEnter: 'modal-open-enter',
    } as const;

    get modalController(): UIModalController {
        return document.querySelector<UIModalController>(
            'ui-modal-controller'
        )!;
    }

    get root(): HTMLElement {
        return document.documentElement;
    }

    /**
     * @returns {UINav | null}
     */
    get mainMenu(): NullOr<UINav> {
        return document.querySelector(this.mainMenuSelector);
    }

    /**
     * Handles different cases when overlay should be shown.
     * @param {CustomEvent} event
     */
    handleOverlay(event: CustomEvent<UIModalUpdateListDetail>): void {
        const modal = event.target as HTMLElement;
        const isMenuActive =
            this.mainMenu?.isActiveMobileMode() ||
            this.mainMenu?.isActiveDesktopMode();

        // 1. If menu is opened / closed
        const openCallback = () => {
            this.opened = true;
            this.root.classList.add(MenuOverlayCSSClass);
        };
        const closeCallback = async () => {
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.root.classList.remove(MenuOverlayCSSClass);
            this.opened = false;
        };
        mainMenuOverlayHandler(this, event, openCallback, () => {
            void closeCallback();
        });

        // 2. Modals are opened as fullscreen and popovers.
        const enterCallback = () => {
            runInNextFrame(
                this.markTopModal.bind(
                    this,
                    event.type as UIOverlayControllerTriggerAction
                )
            );
            this.opened = true;
            if (!isMenuActive) {
                this.root.classList.remove(MenuOverlayCSSClass);
            }
        };
        const exitCallback = async () => {
            runInNextFrame(
                this.markTopModal.bind(
                    this,
                    event.type as UIOverlayControllerTriggerAction
                )
            );
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.opened = hasLastOverlay(this.modalController);
            if (isMenuActive) {
                this.root.classList.add(MenuOverlayCSSClass);
            }
        };
        const updateCallback = async () => {
            const action: UIOverlayControllerTriggerAction =
                event.detail.action === 'add'
                    ? UIOverlayController.TriggerAction.OpenEnter
                    : UIOverlayController.TriggerAction.CloseEnter;
            runInNextFrame(this.markTopModal.bind(this, action));
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.opened = hasLastOverlay(this.modalController);
        };
        void modalOverlayHandler(
            this,
            event,
            enterCallback,
            () => void exitCallback(),
            () => void updateCallback()
        );
    }

    /**
     * @param {('modal-open-enter' | 'modal-close-enter')} action
     * @private
     */
    private markTopModal(action: UIOverlayControllerTriggerAction): void {
        const modals = this.modalController.sortModalsByOrder(
            this.modalController.getActiveFullViewModals()
        );
        if (modals.length > 0) {
            modals.forEach((modal) => modal.classList.remove(TopModalCSSClass));
            let modal = modals.pop();
            if (!modal) {
                return;
            }
            if (typeof this.lastModal === 'undefined') {
                this.lastModal = modal;
            }
            if (
                action === UIOverlayController.TriggerAction.CloseEnter &&
                modal.isAnimated() &&
                this.lastModal?.isAnimated() &&
                modal !== this.lastModal &&
                !modal.opened
            ) {
                modal = modals.pop();
            }
            modal?.classList.add(TopModalCSSClass);
        }
    }

    private subscribeEvents(): void {
        OverlayWatchEvents.forEach((name) => {
            eventBus.addEventListener(
                name,
                this.overlayHandler as EventListener
            );
        });
    }

    private unsubscribeEvents(): void {
        OverlayWatchEvents.forEach((name) => {
            eventBus.removeEventListener(
                name,
                this.overlayHandler as EventListener
            );
        });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        appendModalControllerIfRequired();
        this.overlayHandler = this.handleOverlay.bind(this);

        if (!this.root) {
            UIOverlayController.DEBUG_MODE &&
                console.error(
                    `UIOverlayController: root element doesn't exists!`
                );
            return;
        }
        this.subscribeEvents();
        this.mainMenu?.mobileMediaQueryList.addListener((event) => {
            const handleMenuOnResizeCallback = () => {
                this.opened = false;
                this.root.classList.remove(MenuOverlayCSSClass);
            };
            handleMenuOnResize(this, event, handleMenuOnResizeCallback);
        });
        browser.mobileMedia.addListener((event) => {
            const handleTooltipsOnResizeCallback = (
                args: ResizeCallbackArgs<UIOverlayController>
            ): void => {
                this.opened = !!args.isMobileMediaMatches;
                const action: UIOverlayControllerTriggerAction = this.opened
                    ? UIOverlayController.TriggerAction.OpenEnter
                    : UIOverlayController.TriggerAction.CloseEnter;
                this.markTopModal(action);
            };
            handleTooltipsOnResize(this, event, handleTooltipsOnResizeCallback);
        });
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
